/* eslint-disable @stripe-internal/embedded/no-restricted-globals */
import {deserializeError} from 'serialize-error';
import * as Sentry from '@sentry/browser';
import type {FetchApiError} from '@stripe-internal/connect-embedded-lib';
import {getPlatformOrigin} from '@stripe-internal/connect-embedded-lib';
import {rpc as observabilityRpc} from '@sail/observability';
import {fromMessagePort} from '@sail/rpc';
import type {createCache} from '@sail/data';
import type {
  IFrameOpenPopupAndWaitForAuthRequest,
  IFramePopupCompleteResponse,
  IFrameOpenPopupAndWaitForCompleteRequest,
  IFrameAuthStateChangedEvent,
  IFrameExternalPayoutAccountKeyAvailableEvent,
  IFrameAuthStateResponse,
  IFrameAnyMessage,
  IFrameMessageInitResponse,
  IFrameEventMessage,
} from '../data-layer-client/FrameMessenger';
import {assertUnreachable} from '../utils/assertUnreachable';
import {
  isConnectFrameMessage,
  stripeConnectJsMessage,
} from '../utils/isConnectFrameMessage';
import {getDevLogger} from '../utils/getLogger';
import type {PrimitiveRecord} from '../utils/errorHandling';
import {AnalyticsWrapper} from './AnalyticsWrapper';
import {analytics, setAuthMetadata} from './analytics';
import {ErrorReporter} from './sentry/ErrorReporter';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {authStore} from './authentication/AuthenticationStore';
import {isAllowedAuthOrigin} from './authentication/types';
import {claimAccountSession} from './AccountSessionUtil';
import {getFrameResponse} from './proxyRequest';
import type {AnalyticsRecord} from './AnalyticsTypes';
import {R_URL, QA_R_URL} from './MetricsEndpoints';
import {createUserAuthedKey, invalidateSessions} from './UserSessionUtil';
import {DeferredPromise} from '../utils/DeferredPromise';
import {serializeFetchErrorForPostMessage} from '../data-layer-client/utils';
import {logFontFamily} from './FontLogger';
import {collectAndSendRiskSignals} from './RiskSignalsCollector';
import {setServiceEnvironmentOverride} from '../utils/getTargetEnvironment';

declare global {
  interface Window {
    __stripeGlobals: {
      apolloCache: ReturnType<typeof createCache> | undefined;
    };
  }
}

window.__stripeGlobals = {
  apolloCache: undefined,
};

// Metrics endpoints
const devLogger = getDevLogger();

// Assume livemode unless otherwise determined. We want to use
// this value to be able to key off whether the environment is
// livemode or testmode.
// *Do not key off this for livemode if that thing is sensitive!*
let dataLayerFrameLivemode = true;

export function setDataLayerFrameLivemode(livemode: boolean) {
  dataLayerFrameLivemode = livemode;
}

export function getDataLayerFrameLivemode(): boolean {
  return dataLayerFrameLivemode;
}

// Holds metadata sent to analytics and Sentry. Both Analytics and Primitive records
// should have the same type signature. But this ensures we'll always have the correct
// metadata.
type ObservabilityAuthMetadata = AnalyticsRecord & PrimitiveRecord;
let observabilityAuthMetadata: ObservabilityAuthMetadata = {};

export function setObservabilityAuthMetadata(
  metadata: ObservabilityAuthMetadata,
): void {
  observabilityAuthMetadata = metadata;
  setAuthMetadata(observabilityAuthMetadata);
  AnalyticsWrapper.instance.observabilityAuthMetadata =
    observabilityAuthMetadata;
}

export function getObservabilityAuthMetadata(): ObservabilityAuthMetadata {
  return observabilityAuthMetadata;
}

export type ClaimInfo = {
  connectedAccountId?: string;
  livemode?: boolean;
  stripethirdpartyBaseUrl?: string;
  riskSignalsHcaptchaSitekey?: string;
};
let claimInfo: ClaimInfo | undefined;
export const setClaimInfo = (info: ClaimInfo | undefined) => {
  claimInfo = info;
};

export const getClaimInfo = (): ClaimInfo | undefined => claimInfo;

/**
 * Maps message ids to the AbortControllers that need to abort the fetch request.
 */
export const abortControllerMap = new Map<string, AbortController>();

/**
 * Used to keep track of message channels and their origin. The origin is used to know whether the content
 * is rendering on the platform or on the ui layer.
 */
export interface IDatalayerMessageChannel {
  port: MessagePort;
  origin: string;
}

/** The array of listeners that communicate with Connect Elements */
let dataLayerMessageChannels: IDatalayerMessageChannel[] = [];

/**
 * Visible for testing.
 *
 * TODO: It'd probably be nicer if we wrapped the notification stuff in a class.
 * We could create a new instance for each test (so we don't have to reset `port`)
 * and use a singleton instance in the actual implementation.
 */
export const clearMessageChannels = () => {
  dataLayerMessageChannels = [];
};

const notifyAuthState = (
  port: MessagePort,
  {id, type}: IFrameAnyMessage & {id: string},
) => {
  const response: IFrameAuthStateResponse = {
    id,
    type,
    requestType: 'authState',
    response: authStore.state(),
  };

  port.postMessage(response);
};

export const notifyAuthStateChanged = () => {
  const message: IFrameAuthStateChangedEvent = {
    type: stripeConnectJsMessage,
    requestType: 'authStateChanged',
    data: authStore.state(),
    event: true,
  };

  // We notify all port listeners that the auth state has changed
  // eslint-disable-next-line no-restricted-syntax
  for (const channel of dataLayerMessageChannels) {
    const {port} = channel;
    port.postMessage(message);
    if (authPopup && authPopup.requestType === 'openPopupAndWaitForAuth') {
      notifyAuthState(port, {
        id: authPopup.frameRequestId,
        type: stripeConnectJsMessage,
        requestType: 'authState',
      });
    }
  }
};

export const notifyExternalPayoutAccountKeyAvailable = (authToken: string) => {
  const message: IFrameExternalPayoutAccountKeyAvailableEvent = {
    type: stripeConnectJsMessage,
    requestType: 'externalPayoutAccountKeyAvailable',
    data: {authToken},
    event: true,
  };

  // eslint-disable-next-line no-restricted-syntax
  for (const channel of dataLayerMessageChannels) {
    channel.port.postMessage(message);
  }
};

let authPopup: {
  frameRequestId: string;
  requestType: 'openPopupAndWaitForAuth' | 'openPopupAndWaitForComplete';
  authCompleteDeferredPromise: DeferredPromise<void>;
};

const closeAuthPopup = (port: MessagePort) => {
  if (authPopup) {
    const response: IFramePopupCompleteResponse = {
      id: authPopup.frameRequestId,
      type: stripeConnectJsMessage,
      requestType: 'popupComplete',
      response: 'complete',
    };
    port.postMessage(response);
  }
};

const openAuthPopup = (
  port: MessagePort,
  frameRequest:
    | IFrameOpenPopupAndWaitForAuthRequest
    | IFrameOpenPopupAndWaitForCompleteRequest,
): void => {
  const {id, requestType} = frameRequest;
  authPopup = {
    frameRequestId: id,
    requestType,
    authCompleteDeferredPromise: new DeferredPromise(),
  };

  // do not hold sending back popupComplete response if auth is not required (status is already user_authenticated)
  // or when auth completion and popup completion are not simultaneous
  if (
    authStore.state().status === 'user_authenticated' ||
    requestType === 'openPopupAndWaitForComplete'
  ) {
    authPopup.authCompleteDeferredPromise.resolve();
  }

  const abortController = new AbortController();
  abortControllerMap.set(id, abortController);
  abortController.signal.addEventListener('abort', () => closeAuthPopup(port), {
    once: true,
  });
};

/**
 * The data layer can receive a few types of messages directly on the main window.
 *
 * The first message that the data layer expects to receive is from ConnectElements, initializing the
 * MessageChannel between the two. We only ever expect to receive one of these events.
 *
 * The data layer can also receive messages from other Stripe sources (like authentication popups).
 * This is done to ensure secure communication between the data layer and Stripe domains. These messages
 * are filtered to a list of known origins before being processed.
 */
export const onWindowMessageReceived = (event: MessageEvent) => {
  if (!isConnectFrameMessage(event.data)) {
    return;
  }

  // Validate the event comes from the platform, from a stripe allowed auth origin, or from the ui layer
  const originIsInPopupAllowlist = isAllowedAuthOrigin(event.origin);

  const platformOrigin = getPlatformOrigin();
  const messageIsFromUILayer = window.origin === event.origin;
  const messageIsFromPlatform = event.origin === platformOrigin;

  if (
    !originIsInPopupAllowlist &&
    !messageIsFromUILayer &&
    !messageIsFromPlatform
  ) {
    devLogger.error('Received a message from an invalid origin', event.origin);
    return;
  }

  switch (event.data.requestType) {
    case 'init': {
      if (!messageIsFromUILayer && !messageIsFromPlatform) {
        devLogger.error(
          'The init message should come from the platform or the UI layer only',
        );
        return;
      }

      if (event.ports.length === 0) {
        devLogger.error('Did not receive a reply port in init message');
        return;
      }

      const [port, sdkPort] = event.ports;
      const messagePortChannel: IDatalayerMessageChannel = {
        port,
        origin: event.origin,
      };

      const {
        releaseCandidate,
        isAnalyticsDisabled: analyticsDisabled,
        serviceEnvironmentOverride,
      } = event.data;

      if (serviceEnvironmentOverride) {
        setServiceEnvironmentOverride(serviceEnvironmentOverride);
      }

      port.onmessage = createOnMessageChannelEventListener(
        messagePortChannel,
        releaseCandidate,
        analyticsDisabled,
      );

      const reply: IFrameMessageInitResponse = {
        type: stripeConnectJsMessage,
        requestType: 'initresponse',
      };

      // Acknowledge ready to receive events
      messagePortChannel.port.postMessage(reply);

      // Initialize the sdk/observability RPC server
      if (sdkPort != null) {
        sdkPort.start();
        observabilityRpc.initServer({
          transport: fromMessagePort(sdkPort),
        });
      } else {
        devLogger.error('SDK port not provided in "init" message');
      }

      break;
    }
    case 'popupAuthenticationComplete': {
      if (!originIsInPopupAllowlist) {
        devLogger.error(
          'Received an auth event from an invalid origin',
          event.origin,
        );
        return;
      }

      if (event.data.apiKey == null) {
        devLogger.error('Authentication event was missing API key');
        return;
      }

      const handleAuthComplete = (apiKey?: string, refreshToken?: string) => {
        if (apiKey != null) {
          authStore.persist({
            apiKey,
            type: 'stripe_user',
            userSessionRefreshToken: refreshToken,
          });
          analytics.track('submerchant_surfaces_user_key_received', {
            method: 'popup',
          });
          notifyAuthStateChanged();
        }
        authPopup?.authCompleteDeferredPromise.resolve();
      };

      if (authStore.state().status !== 'user_authenticated') {
        const accountSessionKey = authStore.getApiKey('account_session');
        createUserAuthedKey(accountSessionKey?.key || '', event.data)
          .then((response) => {
            handleAuthComplete(
              response.userAuthedEmbeddedKey,
              response.refreshToken,
            );
          })
          .catch((error) => {
            // Notify all listeners that there was an error with auth
            dataLayerMessageChannels.forEach(({port}) =>
              port.postMessage({
                id: authPopup.frameRequestId,
                type: stripeConnectJsMessage,
                requestType: 'fetchErrorResponse',
                error: serializeFetchErrorForPostMessage(
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  error as FetchApiError,
                ),
              }),
            );
            authPopup?.authCompleteDeferredPromise.reject(error);
          });
      } else {
        // if popup is opened after user authenticated, it still sends the popupAuthenticationComplete event
        // but we don't need to create the user-authed key again in this case
        handleAuthComplete();
      }

      break;
    }
    case 'popupComplete': {
      if (!originIsInPopupAllowlist) {
        devLogger.error(
          'Received an auth event from an invalid origin',
          event.origin,
        );
        return;
      }
      if (authPopup) {
        authPopup.authCompleteDeferredPromise.promise
          .then(() => {
            dataLayerMessageChannels.forEach(({port}) => closeAuthPopup(port));
          })
          .catch(() => {});
      } else {
        dataLayerMessageChannels.forEach(({port}) => closeAuthPopup(port));
      }

      break;
    }
    case 'popupReady': {
      const message: IFrameEventMessage<'popupReady'> = {
        type: stripeConnectJsMessage,
        requestType: 'popupReady',
        event: true,
        data: undefined,
      };
      dataLayerMessageChannels.forEach(({port}) => {
        port.postMessage(message);
      });
      return;
    }
    case 'popupLoaded': {
      const message: IFrameEventMessage<'popupLoaded'> = {
        type: stripeConnectJsMessage,
        requestType: 'popupLoaded',
        event: true,
        data: undefined,
      };
      dataLayerMessageChannels.forEach(({port}) => {
        port.postMessage(message);
      });
      return;
    }
    case 'popupDoRefresh': {
      const message: IFrameEventMessage<'popupDoRefresh'> = {
        type: stripeConnectJsMessage,
        requestType: 'popupDoRefresh',
        event: true,
        data: undefined,
      };
      dataLayerMessageChannels.forEach(({port}) => {
        port.postMessage(message);
      });
      return;
    }

    default: {
      devLogger.error('Unexpected frame message', event.data);
    }
  }
};

export const createOnMessageChannelEventListener = (
  messagePortChannel: IDatalayerMessageChannel,
  releaseCandidate: string | undefined,
  analyticsDisabled: boolean | undefined,
) => {
  // Add to the array of port listeners
  dataLayerMessageChannels.push(messagePortChannel);

  return async (event: MessageEvent) => {
    const {data} = event;
    const {port} = messagePortChannel;

    if (!isConnectFrameMessage(data)) {
      // We do not know what to do with this message
      devLogger.warn(
        'Received an unknown message on the message channel',
        JSON.parse(JSON.stringify(data)),
      );
      return;
    }
    const {serviceEnvironment} = getCurrentScriptUrlContext();
    switch (data.requestType) {
      case 'abort': {
        const abortController: AbortController | undefined =
          abortControllerMap.get(data.id);
        if (abortController) {
          abortController.abort();
        }
        break;
      }
      case 'analyticsEvent': {
        AnalyticsWrapper.instance.processEvent(data.analyticsEvent);
        break;
      }
      case 'errorReport': {
        const deserializedError = deserializeError(
          data.errorPayload.serializableError,
        );
        const errorReporter = new ErrorReporter(
          serviceEnvironment,
          releaseCandidate,
          analyticsDisabled,
        );
        errorReporter.captureException({
          error: deserializedError,
          connectElement: data.errorPayload.connectElement,
          invokingComponent: data.errorPayload.invokingComponent,
          tags: Object.assign(
            {},
            observabilityAuthMetadata,
            data.errorPayload.tags,
          ),
          extra: data.errorPayload.extra,
          owner: data.errorPayload.owner,
          level: data.errorPayload.level,
        });
        break;
      }
      case 'metricsEvent': {
        const metrics = {
          ...data.metricsPayload,
          host_env: serviceEnvironment === 'prod' ? 'prod' : 'qa', // Set host_env on metrics;
        };
        await fetch(serviceEnvironment === 'prod' ? R_URL : QA_R_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          // https://github.com/microsoft/TypeScript/issues/32951 - URLSearchParams API only accepts strings
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          body: new URLSearchParams({
            ...observabilityAuthMetadata,
            ...metrics,
          } as any).toString(),
        });
        break;
      }
      case 'init':
      case 'initresponse':
      case 'logIframeLoadedFontResponse':
      case 'claimAccountSessionResponse':
      case 'collectRiskSignalsResponse':
      case 'response':
      case 'errorResponse':
      case 'fetchErrorResponse':
      case 'popupAuthenticationComplete':
      case 'popupComplete':
      case 'popupLoaded':
      case 'popupReady':
      case 'popupDoRefresh':
      case 'authStateChanged':
      case 'externalPayoutAccountKeyAvailable':
      case 'authState': {
        // We shouldn't be getting these events.
        devLogger.error('The data layer received an invalid event type', data);
        break;
      }
      case 'logIframeLoadedFontRequest': {
        const response = logFontFamily(data);
        port.postMessage(response);
        break;
      }
      case 'claimAccountSessionRequest': {
        const sendRetrievedUserKeyAnalytics = () => {
          analytics.track('submerchant_surfaces_user_key_received', {
            method: 'refresh_token',
          });
        };
        const claimAccountSessionResponse = await claimAccountSession(
          data,
          sendRetrievedUserKeyAnalytics,
        );
        port.postMessage(claimAccountSessionResponse);
        if (claimAccountSessionResponse.requestType !== 'errorResponse') {
          notifyAuthStateChanged();
          if (window.performance) {
            const entry = window.performance
              .getEntriesByName(
                `https://${data.request.apiHost}/v1/account_sessions/claim`,
              )
              .at(-1);
            if (entry) {
              analytics.track('submerchant_surfaces_account_session_claim', {
                duration: Math.ceil(entry.duration),
              });
            }
          }
        }
        break;
      }
      case 'collectRiskSignalsRequest': {
        const response = await collectAndSendRiskSignals(data);

        // nosemgrep: ban-postmessage-calls
        port.postMessage(response);
        break;
      }
      case 'authedProxy':
      case 'proxy': {
        // Some other kind of request
        const authStateChangedCallback = () => {
          notifyAuthStateChanged();
        };
        const frameResponse = await getFrameResponse(
          data,
          authStateChangedCallback,
        );

        port.postMessage(frameResponse);
        break;
      }
      case 'authStateRequest': {
        notifyAuthState(port, data);
        break;
      }
      case 'logout': {
        const logoutResult = await invalidateSessions(data);
        if (logoutResult.invalidated) {
          notifyAuthStateChanged();
        }
        if (logoutResult.error) {
          port.postMessage({
            id: data.id,
            type: data.type,
            requestType: 'fetchErrorResponse',
            error: logoutResult.error,
          });
        } else {
          notifyAuthState(port, data);
        }
        break;
      }
      case 'sentryTags':
        Sentry.setTags(data.tags);
        break;
      case 'openPopupAndWaitForComplete':
      case 'openPopupAndWaitForAuth':
        openAuthPopup(port, data);
        break;
      default:
        assertUnreachable(
          data,
          'Unknown message event type received by the data layer',
        );
        break;
    }
  };
};

export const addWindowEventListener = (): void => {
  window.addEventListener('message', onWindowMessageReceived);
};

addWindowEventListener();
